'use client';

import { InputHTMLAttributes, forwardRef, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import styles from './TextField.module.css';
import Icon from '../primitives/Icon';

type TextFieldSize = 'sm' | 'md' | 'lg';
type TextFieldInputType = 'text' | 'email' | 'phone' | 'password' | 'number';

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  fieldSize?: TextFieldSize;
  label?: string;
  helperText?: string;
  error?: boolean;
  errorText?: string;
  maxLength?: number;
  showCharCount?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  clearable?: boolean;
  onClear?: () => void;
  inputType?: TextFieldInputType;
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(({
  fieldSize = 'md',
  label,
  helperText,
  error = false,
  errorText,
  maxLength,
  showCharCount = false,
  leadingIcon,
  trailingIcon,
  clearable = false,
  onClear,
  disabled = false,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  className = '',
  inputType = 'text',
  ...props
}, ref) => {
  const [focused, setFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue?.toString() || '');
  const [validationError, setValidationError] = useState('');
  const [jitter, setJitter] = useState(false);
  const prevErrorRef = useRef(false);

  const currentValue = value !== undefined ? value.toString() : internalValue;
  const hasValue = currentValue.length > 0;

  // Determine effective error state
  const hasError = error || !!validationError;
  const displayErrorText = error && errorText ? errorText : validationError || (error ? errorText : '');
  const displayHelperText = hasError && displayErrorText ? displayErrorText : helperText;

  // Jitter effect when error transitions from false to true
  useEffect(() => {
    if (hasError && !prevErrorRef.current) {
      setJitter(true);
      const timer = setTimeout(() => setJitter(false), 500);
      prevErrorRef.current = hasError;
      return () => clearTimeout(timer);
    }
    prevErrorRef.current = hasError;
  }, [hasError]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;

    if (inputType === 'phone') {
      val = val.replace(/[^0-9+]/g, '');
      // Only allow + at the start
      if (val.indexOf('+') > 0) {
        val = val.charAt(0) + val.slice(1).replace(/\+/g, '');
      }
      e.target.value = val;
    }

    // Clear validation error on edit
    if (validationError) setValidationError('');

    if (value === undefined) {
      setInternalValue(val);
    }
    onChange?.(e);
  }, [onChange, value, inputType, validationError]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    onFocus?.(e);
  }, [onFocus]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    onBlur?.(e);

    const blurValue = value !== undefined ? value.toString() : internalValue;

    if (inputType === 'email' && blurValue) {
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(blurValue);
      setValidationError(valid ? '' : 'Please enter a valid email address');
    }

    if (inputType === 'phone' && blurValue) {
      const valid = /^\+?[0-9]+$/.test(blurValue);
      setValidationError(valid ? '' : 'Please enter a valid phone number');
    }

    if (inputType === 'password' && blurValue) {
      if (blurValue.length < 8) {
        setValidationError('Password must be at least 8 characters');
      } else if (!/[a-zA-Z]/.test(blurValue)) {
        setValidationError('Password must include at least one letter');
      } else if (!/[0-9]/.test(blurValue)) {
        setValidationError('Password must include at least one number');
      } else if (!/[^a-zA-Z0-9]/.test(blurValue)) {
        setValidationError('Password must include at least one symbol');
      } else {
        setValidationError('');
      }
    }
  }, [onBlur, inputType, value, internalValue]);

  const handleClear = useCallback(() => {
    if (value === undefined) {
      setInternalValue('');
    }
    setValidationError('');
    onClear?.();
  }, [onClear, value]);

  // Resolve native input type. `number` gets the native numeric input so
  // amount fields (prices, thresholds) get numeric keyboards and step keys;
  // everything else stays a text input so the field owns its own formatting.
  const nativeType =
    inputType === 'password' ? 'password' : inputType === 'number' ? 'number' : 'text';
  const effectiveMaxLength = inputType === 'password' ? 15 : maxLength;

  const wrapperClasses = [
    styles.wrapper,
    styles[fieldSize],
    focused ? styles.focused : '',
    hasError ? styles.error : '',
    disabled ? styles.disabled : '',
    leadingIcon ? styles.hasLeading : '',
    jitter ? styles.jitter : '',
    className,
  ].filter(Boolean).join(' ');

  // Footer content
  const footer = (displayHelperText || showCharCount) ? (
    <div className={styles.footer}>
      {displayHelperText && <span className={styles.helperText}>{displayHelperText}</span>}
      {showCharCount && effectiveMaxLength && (
        <span className={styles.charCount}>{currentValue.length}/{effectiveMaxLength}</span>
      )}
    </div>
  ) : null;

  return (
    <div className={wrapperClasses}>
      {label && <span className={styles.label}>{label}</span>}

      <div className={styles.field}>
        {leadingIcon && <span className={styles.leadingIcon}>{leadingIcon}</span>}

        <input
          ref={ref}
          type={nativeType}
          className={styles.input}
          disabled={disabled}
          value={value}
          defaultValue={value === undefined ? defaultValue : undefined}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={effectiveMaxLength}
          {...props}
        />

        {clearable && hasValue && !disabled && (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={handleClear}
            tabIndex={-1}
            aria-label="Clear"
          >
            <Icon name="close" size="xs" />
          </button>
        )}

        {trailingIcon && <span className={styles.trailingIcon}>{trailingIcon}</span>}
      </div>

      {footer}
    </div>
  );
});

TextField.displayName = 'TextField';
export default TextField;
